import { Close } from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getDistricts } from 'src/shared/services/districtService';

export interface AssociationWithDistrict {
  countyAssId: number;
  countyAss: string;
  localAssId: number;
  localAss: string;
}

type DistrictAndAssociationPickerProps = {
  callback: (val: number) => void;
};

export const DistrictAndAssociationPicker = (props: DistrictAndAssociationPickerProps) => {
  const { callback } = props;
  const { t } = useTranslation(['common']);
  const isMounted = useIsMounted();

  const [selectedValue, setSelectedValue] = useState<AssociationWithDistrict | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AssociationWithDistrict[]>([]);
  const loading = open && options.length === 0;

  const { pickerHeader, associationName } = useDistrictPickerHeader();

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!options.length) {
      (async () => {
        const result = await getDistricts(0, 10000, 'countyAss', 'asc', true);

        if (active && isMounted()) {
          const data: AssociationWithDistrict[] = [];
          result.data.results.forEach((item) => {
            item.associations.map((x) => {
              data.push({ countyAssId: item.countyAss_id, countyAss: item.countyAss, localAss: x.localAss, localAssId: x.localAss_id });
              return '';
            });
          });

          setOptions(data);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [isMounted, loading, options.length]);

  return (
    <Autocomplete
      autoHighlight
      open={open}
      openOnFocus
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={options.sort((a, b) => -b.countyAss.localeCompare(a.countyAss))}
      groupBy={(option) => option.countyAss}
      getOptionLabel={(option) => {
        return option.localAss;
      }}
      fullWidth
      size='small'
      value={selectedValue}
      onChange={(event: any, newValue: AssociationWithDistrict | null) => {
        setSelectedValue(newValue);
        if (newValue) callback(newValue.localAssId);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{
            shrink: true,
          }}
          placeholder={t('member:createMember.selectAssociation', { associationName })}
          label={pickerHeader}
        />
      )}
      clearIcon={<Close fontSize='small' />}
    />
  );
};
